import { createFileRoute, Link } from "@tanstack/react-router";
import { services } from "@/lib/portfolio";
import { Testimonials } from "@/components/Testimonials";
import { ServiceMatcher } from "@/components/ServiceMatcher";
import { ServiceBentoTile, type TileSize } from "@/components/ServiceBentoTile";
import { DarkSectionBackdrop } from "@/components/DarkSectionBackdrop";
import { LightSectionBackdrop } from "@/components/LightSectionBackdrop";
import { SparkleDivider } from "@/components/SparkleDivider";
import { Check, Compass, TrendingUp, Workflow, X, FileText, type LucideIcon } from "lucide-react";

const SERVICE_META: Record<
  string,
  { icon: LucideIcon; label: string; size: TileSize; span: string }
> = {
  "content-strategy": { icon: Compass, label: "Strategy", size: "md", span: "md:col-span-6" },
  "social-media": { icon: TrendingUp, label: "Growth", size: "md", span: "md:col-span-6" },
  "technical-writing": { icon: FileText, label: "Writing", size: "md", span: "md:col-span-6" },
  "ai-workflows": { icon: Workflow, label: "AI Operations", size: "md", span: "md:col-span-6" },
};

// Four commercially dominant offers, in display order.
const FEATURED_ORDER = [
  "content-strategy",
  "social-media",
  "technical-writing",
  "ai-workflows",
] as const;

// Titles rewritten for the four-pillar framing on the services index only;
// each service detail page keeps its full canonical title.
const FEATURED_TITLES: Record<string, string> = {
  "content-strategy": "Content Strategy & Editorial Systems",
  "social-media": "Social Media & Community Growth",
  "technical-writing": "Technical Writing & Messaging",
  "ai-workflows": "AI-Assisted Content Operations",
};

// Additional capabilities (subordinate to the four pillars).
const ADDITIONAL_CAPABILITIES: ReadonlyArray<{ label: string; slug?: string }> = [
  { label: "Paid Media & Advertising", slug: "paid-advertising" },
  { label: "Newsletter strategy & production", slug: "newsletter" },
  { label: "Website strategy & copy", slug: "web-presence" },
  { label: "Website Design & Management", slug: "web-presence" },
  { label: "Podcast & interview production", slug: "creator-systems" },
  { label: "Content repurposing", slug: "creator-systems" },
  { label: "Marketing audits", slug: "positioning-audits" },
  { label: "Influencer & partner campaigns", slug: "social-media" },
  { label: "Video production & editing", slug: "creator-systems" },
  { label: "Reporting & analytics", slug: "content-strategy" },
  { label: "Personal brand strategy", slug: "positioning-audits" },
  { label: "Brand Messaging & Positioning", slug: "copywriting" },
];

export const Route = createFileRoute("/services")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },

      { title: "Scott Cunningham Services | Content Strategy & AI Workflows" },
      {
        name: "description",
        content:
          "Scott Cunningham Services (@scottcbusiness): content strategy, copywriting, social media, campaigns, and AI workflows for tech brands.",
      },
      { property: "og:title", content: "Scott Cunningham Services | Content Strategy & AI Workflows" },
      {
        property: "og:description",
        content:
          "Scott Cunningham Services (@scottcbusiness): content strategy, copywriting, social media, campaigns, and AI workflows for tech brands.",
      },
      { property: "og:url", content: "https://scottcbusiness.com/services" },
      { name: "twitter:title", content: "Scott Cunningham Services | Content Strategy & AI Workflows" },
      {
        name: "twitter:description",
        content:
          "Scott Cunningham Services (@scottcbusiness): content strategy, copywriting, social media, campaigns, and AI workflows for tech brands.",
      },
    ],
    links: [{ rel: "canonical", href: "https://scottcbusiness.com/services" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "ItemList",
              name: "Services offered by Scott Cunningham",
              itemListOrder: "https://schema.org/ItemListOrderAscending",
              itemListElement: services.map((s, i) => ({
                "@type": "ListItem",
                position: i + 1,
                url: `https://scottcbusiness.com/services/${s.slug}`,
                item: {
                  "@type": "Service",
                  "@id": `https://scottcbusiness.com/services/${s.slug}`,
                  name: s.title,
                  description: s.body,
                  url: `https://scottcbusiness.com/services/${s.slug}`,
                  serviceType: s.title,
                  areaServed: "Worldwide",
                  provider: { "@id": "https://scottcbusiness.com/#person" },
                },
              })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://scottcbusiness.com/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Services",
                  item: "https://scottcbusiness.com/services",
                },
              ],
            },
            {
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: "What does a content strategist do for technical brands?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "A content strategist for technical brands maps audience, narrative, and channels to business outcomes. From there, the editorial system takes shape with positioning, messaging frameworks, content calendars, and reporting that turn complex product details into clear buyer-facing content across blogs, social, sales enablement, and community.",
                  },
                },
                {
                  "@type": "Question",
                  name: "What is AI-assisted content workflow design?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "AI-assisted content workflow design is the practice of building research, drafting, editing, and reporting pipelines that combine human editorial judgement with LLMs and automation, so a small team can produce high-quality content at the cadence of a much larger one without losing brand voice or accuracy.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Do you write for AI, cloud, Web3, and cybersecurity brands?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Yes. Recent and ongoing engagements span AI infrastructure and GPU compute, sovereign and hybrid cloud, permissionless Web3 compute, cybersecurity, SaaS, and consumer technology.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Can you help turn technical product information into marketing content?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Yes. Translating technical depth into clear, persuasive marketing content is the core of the work. That includes positioning, landing pages, blog and whitepaper systems, social and video scripts, sales enablement, and exec ghostwriting.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Do you offer both strategy and execution?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Yes. Engagements typically combine strategy and hands-on execution as a fractional lead, embedded collaborator, or project consultant, with reporting that ties every post back to pipeline, conversion, or retention.",
                  },
                },
                {
                  "@type": "Question",
                  name: "What do project, sprint, fractional, and embedded mean as engagement types?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "A project is a fixed scope with a defined deliverable and end date, like a website rewrite or messaging refresh. A sprint is a short, time-boxed push (typically 2 to 6 weeks) to ship one focused outcome such as an audit, a launch, or a campaign. A fractional engagement is an ongoing part-time leadership role, effectively a head of content or marketing lead a few days a week on retainer. An embedded engagement means working inside your team day to day, in your tools and rituals like a temporary employee, usually for a defined period.",
                  },
                },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: ServicesPage,
});

function ServicesPage() {
  // Build the four featured service objects with rewritten titles / numbering.
  const featuredServices = FEATURED_ORDER.map((slug, i) => {
    const base = services.find((s) => s.slug === slug)!;
    return {
      ...base,
      n: `0${i + 1}`,
      title: FEATURED_TITLES[slug] ?? base.title,
    };
  });

  return (
    <>
      <section className="relative isolate overflow-hidden grain">
        <LightSectionBackdrop variant="dots" arcs={false} />
        <div className="blob float-slow -left-32 -top-24 h-[420px] w-[420px] bg-[var(--color-primary)]" />
        <div
          className="blob float-slow right-[-120px] top-20 h-[360px] w-[360px] bg-[var(--color-electric)]"
          style={{ animationDelay: "-3s" }}
        />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
            Services
          </p>
          <h1 className="mt-6 max-w-4xl font-display leading-[0.95]">
            <span className="block text-[clamp(2.5rem,7vw,6rem)]">
              Four ways <em className="italic text-gradient">I can help.</em>
            </span>
            <span className="mt-4 block text-[clamp(1rem,2vw,1.5rem)] font-normal leading-snug text-muted-foreground">
              Fractional social, content strategy, editorial systems, and AI-assisted production for
              tech brands.
            </span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-muted-foreground">
            These are the four headline offers that anchor most engagements, but they're a slice of
            what I do day to day as a digital marketer. Engagements are scoped around outcomes,
            usually a mix of strategy, hands-on execution, and measurement, as a fractional lead,
            embedded collaborator, or project consultant.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <a
              href="#matcher"
              className="lift-glow group inline-flex items-center gap-2 rounded-full bg-gradient-hero px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-primary-foreground shadow-[0_8px_28px_-6px_oklch(0.62_0.24_28/0.55)]"
            >
              Is Scott a fit?{" "}
              <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
            </a>
            <a
              href="#services-grid"
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground"
            >
              Skip to services
            </a>
          </div>
        </div>
      </section>

      <SparkleDivider />

      <ServiceMatcher />

      <section
        id="services-grid"
        className="relative isolate overflow-hidden bg-foreground text-background scroll-mt-24"
      >
        <DarkSectionBackdrop />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="mb-10">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-background/60">
              Featured services
            </p>
            <h2 className="mt-3 font-display text-3xl leading-[1.05] md:text-5xl">
              Where most engagements <em className="italic text-gradient">start.</em>
            </h2>
            <p className="mt-4 max-w-2xl text-sm text-background/70">
              Each offer stands on its own, and most engagements stack a few of them together as a
              fractional engagement. Hover any tile to spotlight the details.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:auto-rows-fr">
            {featuredServices.map((s, i) => {
              const meta = SERVICE_META[s.slug] ?? {
                icon: Compass,
                label: "Service",
                size: "md" as TileSize,
                span: "md:col-span-6",
              };
              // Checkerboard: alternate accent + filled/outline per row so
              // left and right columns don't share the same treatment.
              const checker = (Math.floor(i / 2) + i) % 2 === 0;
              const accent = checker ? "var(--color-violet)" : "var(--color-electric)";
              const filledButton = checker;
              return (
                <ServiceBentoTile
                  key={s.slug}
                  service={s as never}
                  label={meta.label}
                  icon={meta.icon}
                  accent={accent}
                  size={meta.size}
                  filledButton={filledButton}
                  className={meta.span}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* Everything else */}
      <section className="relative isolate overflow-hidden border-b border-border">
        <LightSectionBackdrop variant="blueprint" arcs={false} sparkles={false} />
        <div className="blob -left-40 bottom-0 h-64 w-64 bg-[var(--color-electric)] opacity-15" />
        <div
          className="blob -right-32 -top-16 h-56 w-56 bg-[var(--color-violet)] opacity-15"
          style={{ animationDelay: "-2s" }}
        />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-6 lg:col-span-5 xl:col-span-4">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                Additional capabilities
              </p>
              <h2 className="mt-4 font-display text-4xl leading-[1.05] md:text-5xl">
                And a wider bench of <em className="italic text-gradient">supporting</em> work.
              </h2>
              <p className="mt-6 max-w-md text-base text-muted-foreground">
                Beyond the four headline offers, these are capabilities I fold into engagements as
                needed, usually to round out a launch, a campaign, or a full-funnel content program.
              </p>
            </div>
            <div className="md:col-span-6 lg:col-span-7 xl:col-span-8">
              {(() => {
                const palette = ["var(--color-primary)", "var(--color-electric)"];
                const liClass =
                  "w-full h-full flex items-center justify-center rounded-full border-2 px-6 py-[7px] text-center font-mono text-xs uppercase tracking-[0.18em] transition-all duration-200 hover:-translate-y-0.5 hover:scale-[1.03] hover:bg-[var(--pill-c)] hover:!text-white";
                return (
                  <ul className="grid grid-cols-2 gap-3" style={{ gridAutoRows: "1fr" }}>
                    {ADDITIONAL_CAPABILITIES.map((s, i) => {
                      const c = palette[i % palette.length];
                      return (
                        <li
                          key={`${s.label}-${i}`}
                          className="flex"
                          style={{ ["--pill-c" as never]: c }}
                        >
                          {s.slug ? (
                            <Link
                              to="/services/$slug"
                              params={{ slug: s.slug }}
                              className={liClass}
                              style={{ borderColor: c, color: c }}
                            >
                              {s.label}
                            </Link>
                          ) : (
                            <span className={liClass} style={{ borderColor: c, color: c }}>
                              {s.label}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                );
              })()}
              <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Don't see what you need?{" "}
                <Link
                  to="/contact"
                  className="underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground"
                >
                  Just ask
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      <SparkleDivider />

      {/* Best fit */}
      <section className="relative isolate overflow-hidden border-b border-border">
        <LightSectionBackdrop variant="analytics" arcs={false} sparkles={false} />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-5 lg:col-span-4 xl:col-span-3">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                Fit check
              </p>
              <h2 className="mt-4 font-display text-4xl leading-[1.05] md:text-5xl">
                Who I'm <em className="italic text-gradient">best for</em> and not.
              </h2>
            </div>
            <div className="md:col-span-7 lg:col-span-8 xl:col-span-9">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="rounded-md border-2 border-foreground bg-background p-5 shadow-pop transition-all duration-300 ease-out hover:-translate-y-1 hover:scale-[1.02] hover:shadow-[0_22px_48px_-18px_oklch(0.16_0.02_280/0.5)]">
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--color-electric)]">
                    Best fit if you need
                  </p>
                  <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-electric)]" />
                      <span>A content lead who can think and execute</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-electric)]" />
                      <span>Technical subjects made clearer</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-electric)]" />
                      <span>Social strategy tied to outcomes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-electric)]" />
                      <span>AI-assisted workflows without generic AI slop</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-electric)]" />
                      <span>
                        Someone who understands Web3, AI, cloud, privacy, and platform risk
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="rounded-md border-2 border-foreground bg-background p-5 shadow-pop transition-all duration-300 ease-out hover:-translate-y-1 hover:scale-[1.02] hover:shadow-[0_22px_48px_-18px_oklch(0.16_0.02_280/0.5)]">
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                    Probably not best fit if you need
                  </p>
                  <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <X className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span>High-volume generic posting</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <X className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span>Viral hacks</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <X className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span>A faceless agency</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <X className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span>Content with no strategy, reporting, or distribution plan</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Testimonials variant="dark" eyebrow="Testimonials" />

      {/* FAQ */}
      <section
        id="faq"
        className="relative isolate overflow-hidden border-t border-border scroll-mt-24"
      >
        <LightSectionBackdrop variant="dots" arcs={false} sparkles={false} />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                FAQ
              </p>
              <h2 className="mt-4 font-display text-4xl leading-[1.05] md:text-5xl">
                Common <em className="italic text-gradient">questions.</em>
              </h2>
              <p className="mt-6 max-w-md text-base text-muted-foreground">
                A few things prospective clients usually want to know before we scope. See{" "}
                <Link
                  to="/work"
                  className="underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground"
                >
                  case studies
                </Link>{" "}
                for proof, or{" "}
                <Link
                  to="/contact"
                  className="underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground"
                >
                  get in touch
                </Link>
                .
              </p>
            </div>
            <dl className="md:col-span-8 border-y border-border">
              {[
                {
                  q: "What does a content strategist do for technical brands?",
                  a: "Maps audience, narrative, and channels to business outcomes. The editorial system covers positioning, messaging frameworks, calendars, and reporting that turn complex product details into clear buyer-facing content across blogs, social, sales enablement, and community.",
                },
                {
                  q: "What is AI-assisted content workflow design?",
                  a: "Building research, drafting, editing, and reporting pipelines that combine human editorial judgement with LLMs and automation, so a small team can ship at the cadence of a much larger one without losing brand voice or accuracy.",
                },
                {
                  q: "Do you write for AI, cloud, Web3, and cybersecurity brands?",
                  a: "Yes. Recent and ongoing engagements span AI infrastructure and GPU compute, sovereign and hybrid cloud, permissionless Web3 compute, cybersecurity, SaaS, and consumer technology.",
                },
                {
                  q: "Can you help turn technical product information into marketing content?",
                  a: "Yes. Translating technical depth into clear, persuasive marketing content is the core of the work, including positioning, landing pages, blog and whitepaper systems, social and video scripts, sales enablement, and exec ghostwriting.",
                },
                {
                  q: "Do you offer both strategy and execution?",
                  a: "Yes. Engagements combine strategy and hands-on execution as a fractional lead, embedded collaborator, or project consultant, with reporting that ties every post back to pipeline, conversion, or retention.",
                },
                {
                  q: "What do project, sprint, fractional, and embedded mean?",
                  a: "Project: a fixed scope with a defined deliverable and end date (e.g. a messaging refresh or website rewrite). Sprint: a short, time-boxed push, typically 2 to 6 weeks, to ship one focused outcome like an audit, launch, or campaign. Fractional: an ongoing part-time leadership role on retainer, effectively a head of content or marketing lead a few days a week. Embedded: working inside your team day to day, in your tools and rituals like a temporary employee, usually for a defined period.",
                },
              ].map((f) => (
                <div
                  key={f.q}
                  className="faq-item grid gap-3 py-6 md:grid-cols-12 border-b border-border last:border-b-0 px-2 -mx-2"
                >
                  <dt className="md:col-span-5 font-display text-xl leading-snug">{f.q}</dt>
                  <dd className="md:col-span-7 text-base leading-relaxed text-muted-foreground">
                    {f.a}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <SparkleDivider />

      <section className="relative isolate overflow-hidden bg-gradient-acid">
        <LightSectionBackdrop variant="blueprint" arcs={false} sparkles={false} />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="mb-6 grid gap-6 md:grid-cols-2">
            <div className="flex flex-col rounded-md border-2 border-foreground bg-background p-8 shadow-pop md:p-10">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                Free diagnostic
              </p>
              <h2 className="mt-4 font-display text-2xl md:text-3xl">
                Is your content system <em className="italic text-gradient">actually working?</em>
              </h2>
              <p className="mt-4 text-base text-muted-foreground">
                Take the Content System Readiness Score to see whether your brand is stuck in random
                posting, reactive execution, planned content, or ready to optimize a real content
                engine.
              </p>
              <div className="mt-auto pt-6">
                <Link
                  to="/tools/content-system-score"
                  className="group inline-flex items-center justify-center gap-2 self-start rounded-full border-2 border-foreground bg-foreground px-6 py-3 font-mono text-xs uppercase leading-none tracking-[0.2em] text-background transition-colors hover:bg-background hover:text-foreground"
                >
                  <span>Check Your Content System</span>
                  <span className="inline-block transition-transform group-hover:translate-x-1">
                    →
                  </span>
                </Link>
              </div>
            </div>
            <div className="flex flex-col rounded-md border-2 border-foreground bg-background p-8 shadow-pop md:p-10">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                Free diagnostic
              </p>
              <h2 className="mt-4 font-display text-2xl md:text-3xl">
                Does your profile <em className="italic text-gradient">build trust?</em>
              </h2>
              <p className="mt-4 text-base text-muted-foreground">
                Run your LinkedIn, X, founder bio, or About section through the Social Profile Trust
                Scan and find out what your profile is really signaling.
              </p>
              <div className="mt-auto pt-6">
                <Link
                  to="/tools/social-profile-scan"
                  className="group inline-flex items-center justify-center gap-2 self-start rounded-full border-2 border-foreground bg-foreground px-6 py-3 font-mono text-xs uppercase leading-none tracking-[0.2em] text-background transition-colors hover:bg-background hover:text-foreground"
                >
                  <span>Scan Your Profile</span>
                  <span className="inline-block transition-transform group-hover:translate-x-1">
                    →
                  </span>
                </Link>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-md border-2 border-foreground bg-background px-6 py-5 shadow-pop md:px-8">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <p className="font-display text-lg md:text-xl">
                Have a project in mind?{" "}
                <em className="italic text-gradient">Let's scope it together.</em>
              </p>
            </div>
            <Link
              to="/contact"
              className="lift-glow group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-hero px-5 py-2.5 font-mono text-[11px] uppercase leading-none tracking-[0.2em] text-primary-foreground shadow-[0_8px_28px_-6px_oklch(0.62_0.24_28/0.55)]"
            >
              <span>Start the conversation</span>
              <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
